/**
 * @file pages/well/SizePumpPage.js
 * @description Well Butler — "Size a pump" (plan 06 §C.2 #3).
 *
 * Guided inputs → POST /api/v1/well/sizing → recommended pump (HP + GPM), the
 * pressure/lift breakdown, the binding-constraint callout, and velocity read-outs.
 * Every term is defined inline; the result leads with the liability block.
 *
 * Auto-data note: GPS address-suggest / property-facts pre-fill are a later step
 * (plan 06 §A, provider seam). For now the tech types the measured values — each
 * field says what it means and where to read it.
 */
import React, { useState } from 'react';
import { Button, Input, Card } from '../../design-system';
import { wellApi } from '../../lib/wellApi';
import OrderPanel from './OrderPanel';
import { pushHistory } from '../../lib/wellHistory';
import {
  WellPage, NumberedField, ResultHeader, Assumptions, Disclaimer, ErrorNote, Term,
} from './wellShared';

const num = (v) => (v === '' || v == null ? undefined : Number(v));

export default function SizePumpPage() {
  const [form, setForm] = useState({
    staticLevelFt: '', drawdownFt: '', yieldGpm: '', wellDiameterIn: '6', totalDepthFt: '',
    fixtureCount: '', elevationLiftFt: '0',
    cutInPsi: '40', cutOutPsi: '60',
    pipeMaterial: 'plastic', dropPipeSizeIn: '1', dropPipeLengthFt: '',
    surfaceLineSizeIn: '1', surfaceLineLengthFt: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function run() {
    setLoading(true); setError(null); setResult(null);
    const input = {
      well: {
        staticLevelFt: num(form.staticLevelFt),
        drawdownFt: num(form.drawdownFt),
        yieldGpm: num(form.yieldGpm),
        wellDiameterIn: num(form.wellDiameterIn),
        totalDepthFt: num(form.totalDepthFt),
      },
      house: {
        demandMethod: 'fixtureCount',
        fixtureCount: num(form.fixtureCount),
        elevationLiftFt: num(form.elevationLiftFt),
      },
      pressure: { cutInPsi: num(form.cutInPsi), cutOutPsi: num(form.cutOutPsi) },
      pipe: {
        material: form.pipeMaterial,
        dropPipeSizeIn: num(form.dropPipeSizeIn),
        dropPipeLengthFt: num(form.dropPipeLengthFt),
        surfaceLineSizeIn: num(form.surfaceLineSizeIn),
        surfaceLineLengthFt: num(form.surfaceLineLengthFt),
      },
    };
    try {
      const env = await wellApi.sizePump(input);
      if (env.success) {
        setResult(env.data);
        const p = env.data.recommendedPump || {};
        pushHistory({
          kind: 'size',
          title: p.brand ? `${p.brand} ${p.modelCode || ''} · ${p.hp || '?'} HP` : 'Pump sizing',
          summary: `${env.data.designFlowGpm ?? '?'} GPM · ${env.data.tdhFt ?? '?'} ft of head`,
          confidence: env.data.confidence,
          persistedId: env.meta && env.meta.persistedId,
        });
      } else {
        setError(env.error || { message: 'Could not size a pump from those numbers.' });
      }
    } catch (e) {
      setError({ message: e.message });
    } finally {
      setLoading(false);
    }
  }

  return (
    <WellPage
      title="Size a pump"
      intro="Enter what you measured at the well and the house. You'll get a recommended pump, how hard it has to push, and what's holding the system back."
    >
      {!result && (
        <Card variant="elevated" padding="md">
          <SectionTitle>The well</SectionTitle>

          <NumberedField
            n={1}
            label="Static water level (feet)"
            hint="How far down the water sits when the pump is OFF. Drop a tape or sounder from the top of the casing."
          >
            <Input type="number" inputMode="decimal" value={form.staticLevelFt} onChange={set('staticLevelFt')} rightAddon="ft" />
          </NumberedField>

          <NumberedField
            n={2}
            label="Drawdown (feet)"
            hint="How much further the water drops once the pump runs a while. Pumping level minus static level."
          >
            <Input type="number" inputMode="decimal" value={form.drawdownFt} onChange={set('drawdownFt')} rightAddon="ft" />
          </NumberedField>

          <NumberedField
            n={3}
            label="Well yield (gallons per minute)"
            hint="How fast the well refills — the most water it can give you without running dry."
          >
            <Input type="number" inputMode="decimal" value={form.yieldGpm} onChange={set('yieldGpm')} rightAddon="GPM" />
          </NumberedField>

          <NumberedField n={4} label="Casing size (inches)" hint="Inside width of the well casing — most are 4 or 6 inch.">
            <Input type="number" inputMode="decimal" value={form.wellDiameterIn} onChange={set('wellDiameterIn')} rightAddon="in" />
          </NumberedField>

          <NumberedField n={5} label="Total well depth (feet)" hint="Drilled depth, if you know it. Optional — helps with storage math.">
            <Input type="number" inputMode="decimal" value={form.totalDepthFt} onChange={set('totalDepthFt')} rightAddon="ft" />
          </NumberedField>

          <SectionTitle>The house</SectionTitle>

          <NumberedField
            n={6}
            label="Fixture count"
            hint="Count the water outlets: each sink, shower, toilet, hose bib, washer, etc. Drives how much water the home needs at once."
          >
            <Input type="number" inputMode="numeric" value={form.fixtureCount} onChange={set('fixtureCount')} />
          </NumberedField>

          <NumberedField
            n={7}
            label="Elevation lift to the house (feet)"
            hint="How much higher the house sits above the wellhead. 0 if it's level or downhill."
          >
            <Input type="number" inputMode="decimal" value={form.elevationLiftFt} onChange={set('elevationLiftFt')} rightAddon="ft" />
          </NumberedField>

          <SectionTitle>Pressure tank settings</SectionTitle>

          <NumberedField n={8} label="Cut-in pressure" hint="The PSI where the pump kicks ON. Usually 40.">
            <Input type="number" inputMode="numeric" value={form.cutInPsi} onChange={set('cutInPsi')} rightAddon="PSI" />
          </NumberedField>

          <NumberedField n={9} label="Cut-out pressure" hint="The PSI where the pump shuts OFF. Usually 60. Must be higher than cut-in.">
            <Input type="number" inputMode="numeric" value={form.cutOutPsi} onChange={set('cutOutPsi')} rightAddon="PSI" />
          </NumberedField>

          <SectionTitle>The pipe</SectionTitle>

          <NumberedField n={10} label="Pipe material">
            <select value={form.pipeMaterial} onChange={set('pipeMaterial')} style={selectStyle}>
              <option value="plastic">Plastic (PVC / poly / PEX)</option>
              <option value="steel">Galvanized / steel</option>
              <option value="copper">Copper</option>
            </select>
          </NumberedField>

          <NumberedField n={11} label="Drop pipe size (inches)" hint="The pipe running down the well to the pump.">
            <Input type="number" inputMode="decimal" value={form.dropPipeSizeIn} onChange={set('dropPipeSizeIn')} rightAddon="in" />
          </NumberedField>

          <NumberedField n={12} label="Drop pipe length (feet)" hint="Usually about the pump-setting depth.">
            <Input type="number" inputMode="decimal" value={form.dropPipeLengthFt} onChange={set('dropPipeLengthFt')} rightAddon="ft" />
          </NumberedField>

          <NumberedField n={13} label="Surface line size (inches)" hint="The pipe from the well over to the house/tank.">
            <Input type="number" inputMode="decimal" value={form.surfaceLineSizeIn} onChange={set('surfaceLineSizeIn')} rightAddon="in" />
          </NumberedField>

          <NumberedField n={14} label="Surface line length (feet)" hint="Horizontal run from the well to the pressure tank.">
            <Input type="number" inputMode="decimal" value={form.surfaceLineLengthFt} onChange={set('surfaceLineLengthFt')} rightAddon="ft" />
          </NumberedField>

          <Button fullWidth size="lg" loading={loading} onClick={run} style={{ marginTop: 'var(--space-2)' }}>
            Size the pump
          </Button>
        </Card>
      )}

      {error && <div style={{ marginTop: 'var(--space-4)' }}><ErrorNote error={error} /></div>}

      {result && <SizeResult data={result} form={form} onReset={() => { setResult(null); setError(null); }} />}
    </WellPage>
  );
}

function SizeResult({ data, form, onReset }) {
  const p = data.recommendedPump || {};
  const c = data.tdhComponents || {};
  const wc = data.wellConstraint || {};
  const answer = p.brand
    ? `${p.brand} ${p.modelCode || ''}`.trim()
    : 'Pump recommendation';
  const sub = `${p.hp ?? '?'} HP · ${p.gpmClass ?? data.designFlowGpm ?? '?'} GPM class`;

  return (
    <div>
      <ResultHeader answer={answer} sub={sub} confidence={data.confidence} />

      <Card variant="elevated" padding="md" style={{ marginBottom: 'var(--space-3)' }}>
        <SectionTitle>What it has to push against</SectionTitle>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body-sm)', marginTop: 0 }}>
          <Term word="Total Dynamic Head (TDH)">
            The total lift the pump fights — the water level it lifts from, plus the height to the house,
            plus the pressure you want, plus friction in the pipe. Measured in feet.
          </Term>{' '}
          for this job is <strong>{data.tdhFt} ft</strong> at <strong>{data.designFlowGpm} GPM</strong>.
        </p>
        <Row label="Lift from the pumping water level" value={`${c.pumpingLevelFt ?? '?'} ft`} />
        <Row label="Height up to the house" value={`${c.elevationLiftFt ?? 0} ft`} />
        <Row label="Pressure you asked for" value={`${c.pressureHeadFt ?? '?'} ft`} />
        <Row label="Friction in the pipe" value={`${c.frictionLossFt ?? '?'} ft`} />
      </Card>

      {wc.constrained && (
        <Card variant="outline" padding="md" style={{ borderColor: 'var(--border-warning, var(--border-strong))', marginBottom: 'var(--space-3)' }}>
          <SectionTitle>The limit on this system</SectionTitle>
          <p style={{ margin: 0, color: 'var(--text-primary)', fontSize: 'var(--text-body-sm)', lineHeight: 1.5 }}>
            {wc.bindingNote || `The well only gives ${wc.yieldGpm} GPM, so it — not the pump — sets the ceiling.`}
          </p>
          {wc.recommendedStorageGal != null && (
            <p style={{ marginTop: 'var(--space-2)', color: 'var(--text-secondary)', fontSize: 'var(--text-body-sm)' }}>
              Consider about <strong>{wc.recommendedStorageGal} gal</strong> of stored water to cover peak use.
            </p>
          )}
        </Card>
      )}

      {Array.isArray(data.velocity) && data.velocity.length > 0 && (
        <Card variant="elevated" padding="md" style={{ marginBottom: 'var(--space-3)' }}>
          <SectionTitle>Water speed in the pipe</SectionTitle>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body-sm)', marginTop: 0 }}>
            Too fast (over ~5 ft/sec) wears pipe and adds friction. Flags below mean the pipe may be undersized.
          </p>
          {data.velocity.map((v) => (
            <Row
              key={v.segment}
              label={v.segment === 'dropPipe' ? 'Drop pipe' : 'Surface line'}
              value={`${v.ftps} ft/sec${v.overHardMax ? '  ⚠ too fast' : v.overDesign ? '  • a bit fast' : ''}`}
            />
          ))}
        </Card>
      )}

      {Array.isArray(data.alternates) && data.alternates.length > 0 && (
        <Card variant="outline" padding="md" style={{ marginBottom: 'var(--space-3)' }}>
          <SectionTitle>Other pumps that fit</SectionTitle>
          {data.alternates.slice(0, 3).map((a, i) => (
            <Row key={i} label={`${a.brand || ''} ${a.modelCode || ''}`.trim() || 'Alternate'} value={`${a.hp ?? '?'} HP`} />
          ))}
        </Card>
      )}

      {data.recommendedPump && <OrderPanel data={data} form={form} />}

      <Assumptions assumptions={data.assumptions} />
      <Disclaimer text={data.disclaimer} />

      <Button fullWidth variant="secondary" size="lg" onClick={onReset} style={{ marginTop: 'var(--space-4)' }}>
        Size another
      </Button>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontWeight: 'var(--weight-bold)', margin: 'var(--space-4) 0 var(--space-3)', fontSize: 'var(--text-body-md)' }}>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', padding: 'var(--space-1) 0', fontSize: 'var(--text-body-sm)' }}>
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ color: 'var(--text-primary)', fontWeight: 'var(--weight-medium)', textAlign: 'right' }}>{value}</span>
    </div>
  );
}

const selectStyle = {
  width: '100%', minHeight: 'var(--input-height-md, 44px)', padding: '0 var(--space-3)',
  borderRadius: 'var(--radius-lg)', border: '2px solid var(--border-default)',
  background: 'var(--surface-raised)', color: 'var(--text-primary)', font: 'inherit',
};
